import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyStaff, updateMyStaff, listSubjects } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Hồ sơ của chính GV/cán bộ — chỉ sửa được thông tin của mình.
// Danh sách + nhãn khớp với PROFILE_TEXT_FIELDS bên trang Nhân sự (StaffListPage.js)
// để cả 2 nơi hiện đúng cùng 1 bộ trường.
const FIELDS = [
  { key: "name", label: "Họ và tên", required: true },
  { key: "employmentType", label: "Hình thức HĐ / Loại lao động", type: "select", options: ["Cơ hữu", "Cơ hữu theo tiết", "Thỉnh giảng"], required: true },
  { key: "currentUnit", label: "Đơn vị đang công tác", showIf: (f) => f.employmentType === "Thỉnh giảng", required: true },
  { key: "workAddress", label: "Địa chỉ nơi công tác", showIf: (f) => f.employmentType === "Thỉnh giảng", required: true },
  { key: "subjectIds", label: "Bộ môn dạy (chọn được nhiều môn)", type: "subjects", full: true, required: true },
  { key: "experienceKhois", label: "Có kinh nghiệm giảng dạy (chọn khối)", type: "khois", full: true, required: true },
  { key: "continueNextYear", label: "Tiếp tục giảng dạy tại Nhà trường năm học mới", type: "select", options: ["Có", "Tạm dừng"], required: true },
  { key: "maxPeriods", label: "Số tiết mong muốn giảng dạy/tuần", showIf: (f) => f.continueNextYear === "Có", required: true },
  { key: "distanceToSchool", label: "Khoảng cách từ nơi ở đến trường", type: "number", suffix: "km", showIf: (f) => f.continueNextYear === "Có", required: true },
  { key: "proposal", label: "Đề xuất với nhà trường", showIf: (f) => f.continueNextYear === "Có" },
  { key: "contractEnd", label: "Ngày kết thúc HĐ", type: "date", showIf: (f) => f.continueNextYear === "Tạm dừng" },
  { key: "resignDate", label: "Ngày nghỉ việc", type: "date", showIf: (f) => f.continueNextYear === "Tạm dừng" },
  { key: "dob", label: "Ngày sinh", type: "date", required: true },
  { key: "gender", label: "Giới tính", type: "select", options: ["Nam", "Nữ"], required: true },
  { key: "phone", label: "SĐT", required: true },
  { key: "cccd", label: "Số CCCD", required: true },
  { key: "cccdIssueDate", label: "Ngày cấp CCCD", type: "date" },
  { key: "relative1", label: "Người thân 1", required: true },
  { key: "relative1Phone", label: "SĐT người thân 1", required: true },
  { key: "contractStart", label: "Ngày bắt đầu HĐ", type: "date" },
  { key: "workStartDate", label: "Ngày bắt đầu làm việc", type: "date" },
  { key: "status", label: "Trạng thái" },
  { key: "specialty", label: "Chuyên môn" },
  { key: "taxCode", label: "MST" },
  { key: "bankName", label: "Ngân hàng nhận lương", type: "select", options: ["Agribank", "BIDV", "Vietcombank", "Vietinbank", "Techcombank", "MB Bank", "TPBank", "Ngân hàng khác"] },
  { key: "bankAccount", label: "Số tài khoản" },
  { key: "homeAddress", label: "Địa chỉ nơi ở", required: true },
  { key: "relative2", label: "Người thân 2" },
  { key: "relative2Phone", label: "SĐT người thân 2" },
  { key: "role", label: "Vai trò (phẩy = nhiều)" },
  { key: "note", label: "Ghi chú", type: "textarea", full: true },
];

const KHOIS = ["10", "11", "12"];

const HappySchoolMyProfile = ({ onNeedVerify } = {}) => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [fields, setFields] = useState({});
  const [subjectIds, setSubjectIds] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [experienceKhois, setExperienceKhois] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([getMyStaff(schoolId), listSubjects(schoolId)])
      .then(([d, subs]) => {
        setData(d);
        setFields(Object.fromEntries(FIELDS.map(({ key }) => [key, (d && d[key]) || ""])));
        setSubjectIds((d && d.subjectIds) || []);
        setAllSubjects((subs || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi")));
        setExperienceKhois((d && d.experienceKhois) || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [schoolId]);

  const toggleSubject = (id) => setSubjectIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleKhoi = (k) => setExperienceKhois((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const isFieldEmpty = (f) => {
    if (f.type === "subjects") return subjectIds.length === 0;
    if (f.type === "khois") return experienceKhois.length === 0;
    return !String(fields[f.key] || "").trim();
  };

  const save = async () => {
    setError(""); setMsg("");
    const missing = FIELDS.filter((f) => f.required && (!f.showIf || f.showIf(fields)) && isFieldEmpty(f));
    if (missing.length) {
      setError(`Vui lòng nhập đủ các trường bắt buộc (*): ${missing.map((f) => f.label).join(", ")}.`);
      return;
    }
    setSaving(true);
    try {
      await updateMyStaff(schoolId, { ...fields, subjectIds, experienceKhois });
      setMsg("Đã lưu thông tin.");
      setEditing(false);
      load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  if (!schoolId) return <div style={{ padding: 24 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;
  if (loading) return <div style={{ padding: 24 }}>Đang tải...</div>;
  if (data && data.needVerify) return (
    <div style={{ padding: 24, maxWidth: 520, margin: "0 auto" }}>
      <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e" }}>
        🔒 Bạn cần <b>xác thực thông tin</b> (Họ tên + CCCD + Ngày sinh) trước khi xem và sửa hồ sơ của mình.
        {onNeedVerify && (
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={onNeedVerify} style={primary}>Xác thực thông tin ngay</button>
          </div>
        )}
      </div>
    </div>
  );
  if (data && data.notLinked) return <div style={{ padding: 24, color: "#b45309" }}>Tài khoản chưa liên kết hồ sơ nhân sự. Liên hệ nhà trường để được gán.</div>;

  const visibleFields = FIELDS.filter((f) => !f.showIf || f.showIf(fields));
  const subjectName = Object.fromEntries(allSubjects.map((s) => [s.id, s.name]));

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>👤 Thông tin của tôi</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        {data && data.department ? `Khối: ${data.department}` : ""} — bạn chỉ sửa được thông tin của chính mình.
      </p>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {msg && <p style={{ color: "#059669" }}>{msg}</p>}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 18, background: "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {visibleFields.map((f) => {
            const { key, label, type, options, full, suffix, required } = f;
            const empty = editing && required && isFieldEmpty(f);
            return (
            <label key={key} style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4, gridColumn: full ? "1 / -1" : undefined }}>
              <span style={{ color: "#4b5563", fontWeight: 600 }}>
                {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
              </span>
              {empty && <span style={{ color: "#dc2626", fontSize: 12 }}>Bắt buộc nhập</span>}
              {type === "subjects" ? (
                editing ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", padding: "8px 9px", border: "1px solid #d1d5db", borderRadius: 6 }}>
                    {allSubjects.map((s) => (
                      <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={subjectIds.includes(s.id)} onChange={() => toggleSubject(s.id)} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "8px 9px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, minHeight: 18 }}>
                    {subjectIds.length ? subjectIds.map((id) => subjectName[id] || id).join(", ") : "—"}
                  </div>
                )
              ) : type === "khois" ? (
                editing ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", padding: "8px 9px", border: "1px solid #d1d5db", borderRadius: 6 }}>
                    {KHOIS.map((k) => (
                      <label key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={experienceKhois.includes(k)} onChange={() => toggleKhoi(k)} />
                        Khối {k}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "8px 9px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, minHeight: 18 }}>
                    {experienceKhois.length ? experienceKhois.map((k) => `Khối ${k}`).join(", ") : "—"}
                  </div>
                )
              ) : editing ? (
                type === "select" ? (
                  <select value={fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: "#fff" }}>
                    <option value="">— Chọn —</option>
                    {options.map((o) => (<option key={o} value={o}>{o}</option>))}
                  </select>
                ) : type === "textarea" ? (
                  <textarea value={fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))} rows={3}
                    style={{ padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
                ) : type === "number" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="number" min={1} step={1} value={fields[key]}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const v = raw === "" ? "" : String(Math.max(1, Math.trunc(Number(raw)) || 1));
                        setFields((f) => ({ ...f, [key]: v }));
                      }}
                      style={{ padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: "100%" }} />
                    {suffix && <span style={{ color: "#6b7280", fontSize: 13 }}>{suffix}</span>}
                  </div>
                ) : type === "date" ? (
                  <input type="date" value={fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} />
                ) : (
                  <input value={fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} />
                )
              ) : (
                <div style={{ padding: "8px 9px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, minHeight: 18, whiteSpace: type === "textarea" ? "pre-wrap" : "normal" }}>
                  {fields[key] ? `${fields[key]}${suffix ? ` ${suffix}` : ""}` : "—"}
                </div>
              )}
            </label>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          {editing ? (
            <>
              <button type="button" onClick={() => { setEditing(false); setFields(Object.fromEntries(FIELDS.map(({ key }) => [key, (data && data[key]) || ""]))); setSubjectIds((data && data.subjectIds) || []); setExperienceKhois((data && data.experienceKhois) || []); }} style={ghost}>Huỷ</button>
              <button type="button" onClick={save} disabled={saving} style={primary}>{saving ? "Đang lưu..." : "Lưu"}</button>
            </>
          ) : (
            <button type="button" onClick={() => setEditing(true)} style={primary}>Sửa thông tin</button>
          )}
        </div>
      </div>
    </div>
  );
};

const primary = { padding: "8px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 };
const ghost = { padding: "8px 14px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer" };

export default HappySchoolMyProfile;
